import { Injectable, inject } from '@angular/core';
import { Firestore, docData } from '@angular/fire/firestore';
import { doc } from 'firebase/firestore';
import { BehaviorSubject, Observable, map, shareReplay } from 'rxjs';

import { StatusEnhanced } from '../models/sap/status-enhanced.model';
import { CollectionsService } from './collections.service';

@Injectable({
  providedIn: 'root',
})
export class UserStatusService {
  private firestore = inject(Firestore);
  private collectionsService = inject(CollectionsService);

  // BehaviorSubject to manage user status state
  private userStatusSubject = new BehaviorSubject<StatusEnhanced | null>(null);
  public userStatus$ = this.userStatusSubject.asObservable();

  // Convenience getter for current status
  get currentStatus(): StatusEnhanced | null {
    return this.userStatusSubject.value;
  }

  // Check if user is active
  get isActive(): boolean {
    return this.currentStatus?.account_status === 'Active';
  }

  // Check if user is inactive
  get isInactive(): boolean {
    return this.currentStatus?.account_status === 'Inactive';
  }

  // Check if user is locked
  get isLocked(): boolean {
    return this.currentStatus?.account_status === 'Locked';
  }

  /**
   * Get user status from Firestore
   * @param userId - The user ID
   * @returns Observable of user status
   */
  public getUserStatus$(userId: string): Observable<StatusEnhanced> {
    const docPath = `${this.collectionsService.userStatusCol()}/${userId}`;
    const userStatusDocRef = doc(this.firestore, docPath);

    return docData(userStatusDocRef, { idField: 'id' }).pipe(
      map((data) => {
        if (!data) {
          // If no status document exists, assume active
          const defaultStatus: StatusEnhanced = {
            id: userId,
            account_status: 'Active',
          };
          this.userStatusSubject.next(defaultStatus);
          return defaultStatus;
        }

        const status = data as StatusEnhanced;
        this.userStatusSubject.next(status);
        return status;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  /**
   * Update the user status in the BehaviorSubject
   * @param status - The new user status
   */
  public updateUserStatus(status: StatusEnhanced | null): void {
    this.userStatusSubject.next(status);
  }

  /**
   * Clear user status (useful for logout)
   */
  public clearUserStatus(): void {
    this.userStatusSubject.next(null);
  }
}
